import { onMounted } from 'vue';
import type { ISubscriber, IEventAggregationService } from '@studyportals/event-aggregation-service-interface';
import { AnonymousStudentServiceReady, AnonymousStudentProfileUpdated, AnonymousStudentProfileSynced, type IAnonymousStudentService, StudentRepositoryStateType } from '@studyportals/anonymous-student-interfaces';
import { CurrencyConversionServiceReadyEvent } from "@studyportals/portal-localized-content";
import { StudentField } from '@studyportals/studentdomain';
import { useSearchStore } from '@stores/useSearchStore';

export const useLocalizedContent = (): void => {
	const searchStore = useSearchStore();
	let anonymousStudent: IAnonymousStudentService | undefined;
	let handler: ISubscriber<{ eventType: string }> | null = null;

	onMounted(() => {
		const eventAggregationService: IEventAggregationService | undefined =
			(window as Record<string, any>)['EventAggregationService'] as IEventAggregationService;

		if (!eventAggregationService) {
			console.warn('EventAggregationService not available');
			return;
		}

		const handleAnonymousStudentReadyEvent = async (event: AnonymousStudentServiceReady): Promise<void> => {
			anonymousStudent = event.anonymousStudentService;
			const data = await anonymousStudent.getStudentData([
				StudentField.NATIONALITY_COUNTRY_ISO,
				StudentField.CURRENCY,
			]);

			const nationalityCountryId = data[StudentField.NATIONALITY_COUNTRY_ISO];
			if (nationalityCountryId) {
				searchStore.$state.userCountryIso = nationalityCountryId.toString();
			}

			const currency = data[StudentField.CURRENCY];
			if (currency) {
				searchStore.$state.userCurrency = currency.toString();
			}
		};

		const handleAnonymousStudentProfileUpdateEvent = async (event: AnonymousStudentProfileUpdated): Promise<void> => {
			const changes = event.changes;
			if (!changes[StudentField.NATIONALITY_COUNTRY_ISO] && !changes[StudentField.CURRENCY]) {
				return;
			}

			const nationalityIso = changes[StudentField.NATIONALITY_COUNTRY_ISO] ? changes[StudentField.NATIONALITY_COUNTRY_ISO].toString() : undefined;

			await searchStore.processCurrencyOrCountryChange(nationalityIso, changes[StudentField.CURRENCY]);
		};

		const handleAnonymousStudentProfileSynced = async (event: AnonymousStudentProfileSynced): Promise<void> => {
			if (!anonymousStudent || event.state !== StudentRepositoryStateType.ONLINE) {
				return;
			}

			const data = await anonymousStudent.getStudentData([StudentField.NATIONALITY_COUNTRY_ISO, StudentField.CURRENCY]);
			const nationalityIso = data[StudentField.NATIONALITY_COUNTRY_ISO] ? data[StudentField.NATIONALITY_COUNTRY_ISO].toString() : undefined;

			await searchStore.processCurrencyOrCountryChange(nationalityIso, data[StudentField.CURRENCY]);
		};

		const handleCurrencyConversionServiceReadyEvent = (event: CurrencyConversionServiceReadyEvent): void => {
			searchStore.$state.currencyConversionService = event.currencyConversionService;
		};

		handler = {
			notify: async (event: { eventType: string }): Promise<void> => {
				switch(event.eventType) {
				case AnonymousStudentServiceReady.EventType:
					await handleAnonymousStudentReadyEvent(event as AnonymousStudentServiceReady);
					break;
				case AnonymousStudentProfileUpdated.EventType:
					await handleAnonymousStudentProfileUpdateEvent(event as AnonymousStudentProfileUpdated);
					break;
				case AnonymousStudentProfileSynced.EventType:
					await handleAnonymousStudentProfileSynced(event as AnonymousStudentProfileSynced);
					break;
				case CurrencyConversionServiceReadyEvent.EventType:
					handleCurrencyConversionServiceReadyEvent(event as CurrencyConversionServiceReadyEvent);
					break;
				}
			}
		};

		eventAggregationService.subscribeTo(AnonymousStudentServiceReady.EventType, handler, true);
		eventAggregationService.subscribeTo(AnonymousStudentProfileUpdated.EventType, handler, true);
		eventAggregationService.subscribeTo(AnonymousStudentProfileSynced.EventType, handler, true);
		eventAggregationService.subscribeTo(CurrencyConversionServiceReadyEvent.EventType, handler, true);
	});
}